<template>
  <div class="gr-page">
    <div class="gr-hero-header">
      <section class="gr-hero">
        <AtsHeroBackground
          class="gr-hero__bg"
          :src="assets.heroBg"
          position="center bottom"
          fallback-color="#0a1628"
        />
        <div class="gr-hero__mask" />
        <div class="gr-hero__content">
          <h1 class="gr-hero__title">全球救援</h1>
          <p class="gr-hero__subtitle">
            平台依托全球60余万个服务网点、3000余名双语星使通过APP、小程序及多端协同系统 为用户提供7×24小时一键求助、实时定位、快速响应，每年服务覆盖人群超5000万。
          </p>
        </div>
      </section>
    </div>

    <!-- 四大能力卡片 -->
    <section class="gr-section gr-section--features" aria-label="核心能力">
      <ul class="gr-features">
        <li v-for="card in featureCards" :key="card.title" class="gr-features__item">
          <img
            class="gr-features__img"
            :src="card.img"
            alt=""
            role="presentation"
            loading="lazy"
            decoding="async"
          >
          <div class="gr-features__body">
            <img
              class="gr-features__icon"
              :src="card.icon"
              alt=""
              role="presentation"
              width="38"
              height="30"
              decoding="async"
            >
            <h3 class="gr-features__title">{{ card.title }}</h3>
            <p class="gr-features__desc">{{ card.desc }}</p>
          </div>
        </li>
      </ul>
    </section>

    <!-- 全球资源布局 -->
    <section class="gr-section gr-section--resource" :style="resourceSectionStyle">
      <h2 class="gr-h2 gr-resource__title">全球资源布局</h2>

      <div class="gr-resource__tabbar" role="tablist" aria-label="资源分类">
        <button
          v-for="tab in resourceTabs"
          :key="tab.id"
          type="button"
          class="gr-resource__tab"
          :class="{ 'is-active': activeResourceTab === tab.id }"
          :style="activeResourceTab === tab.id ? resourceTabActiveStyle : null"
          role="tab"
          :aria-selected="activeResourceTab === tab.id"
          @click="activeResourceTab = tab.id"
        >
          {{ tab.label }}
        </button>
      </div>
      <p class="gr-resource__coverage" :style="resourceCoveragePillStyle">
        <img
          class="gr-resource__dot"
          :src="assets.uiDividerDot"
          alt=""
          role="presentation"
          width="8"
          height="8"
          decoding="async"
        >
        <span class="gr-resource__pill-text">
          覆盖
          <em class="gr-resource__num">{{ resourceCoverage.countries }}</em>
          个国家
          <em class="gr-resource__num">{{ resourceCoverage.cities }}</em>
          个城市
        </span>
      </p>

      <ul class="gr-resource__pills" aria-label="资源指标">
        <li
          v-for="m in resourceMetrics"
          :key="m.prefix"
          class="gr-resource__pill"
          :style="resourceMetricPillStyle"
        >
          <img
            class="gr-resource__dot"
            :src="assets.uiDividerDot"
            alt=""
            role="presentation"
            width="8"
            height="8"
            decoding="async"
          >
          <span class="gr-resource__pill-text">
            {{ m.prefix }}
            <em class="gr-resource__num">{{ m.value }}</em>
            {{ m.suffix }}
          </span>
        </li>
      </ul>
      <div class="gr-resource__stats-wrap">
        <ul class="gr-resource__stats" aria-label="全球资源数据">
          <li
            v-for="s in resourceStats"
            :key="s.label"
            :class="{ 'gr-resource__stat--accent': s.accent }"
          >
            <strong class="gr-resource__stat-value">{{ s.value }}</strong>
            <span class="gr-resource__stat-label">{{ s.label }}</span>
          </li>
        </ul>
      </div>
    </section>

    <!-- 服务标准 -->
    <section class="gr-section gr-section--standards" aria-label="服务标准">
      <h2 class="gr-h2 gr-standards__section-title">服务标准</h2>
      <ul class="gr-standards">
        <li
          v-for="item in serviceStandards"
          :key="item.title"
          class="gr-standards__card"
          :style="standardCardStyle(item)"
        >
          <div class="gr-standards__body">
            <div class="gr-standards__head">
              <h3 class="gr-standards__title">{{ item.title }}</h3>
              <p class="gr-standards__metric">{{ item.metric }}</p>
            </div>
            <p class="gr-standards__desc">{{ item.desc }}</p>
          </div>
        </li>
      </ul>
    </section>

    <!-- 服务优势 -->
    <section class="gr-section gr-section--advantages" :style="advantagesSectionStyle" aria-label="服务优势">
      <h2 class="gr-h2 gr-advantages__section-title">服务优势</h2>
      <ul class="gr-advantages">
        <li
          v-for="(item, index) in serviceAdvantages"
          :key="item.title"
          class="gr-advantages__card"
          :class="{ 'gr-advantages__card--primary': item.primary }"
          :style="advantageCardStyle"
        >
          <img
            v-if="item.primary"
            class="gr-advantages__card-accent"
            :src="assets.uiDividerDot"
            alt=""
            role="presentation"
            decoding="async"
          >
          <span class="gr-advantages__num">{{ String(index + 1).padStart(2, '0') }}</span>
          <h3 class="gr-advantages__card-title">{{ item.title }}</h3>
          <p class="gr-advantages__desc">{{ item.desc }}</p>
        </li>
      </ul>
    </section>

    <!-- 服务案例 -->
    <section class="gr-section gr-section--cases" aria-label="服务案例">
      <div class="gr-cases__head">
        <h2 class="gr-h2 gr-cases__section-title">服务案例</h2>
        <a class="gr-cases__more-btn" href="javascript:;">
          更多案例
          <span class="gr-cases__more-btn-icon" aria-hidden="true">
            <svg
              class="gr-cases__more-btn-icon-svg"
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M2.5 2.5L9.5 9.5M9.5 9.5H4.5M9.5 9.5V4.5"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>
        </a>
      </div>
      <ul class="gr-cases">
        <li v-for="c in serviceCases" :key="c.title" class="gr-cases__card">
          <img
            class="gr-cases__cover"
            :src="c.cover"
            :alt="c.title"
            loading="lazy"
            decoding="async"
          >
          <div class="gr-cases__body" :style="caseBodyStyle">
            <h3 class="gr-cases__title">{{ c.title }}</h3>
            <p class="gr-cases__desc">{{ c.desc }}</p>
            <a class="gr-cases__link" href="javascript:;">
              更多案例
              <img
                class="gr-cases__link-icon"
                :src="assets.iconArrowRightSmall"
                alt=""
                role="presentation"
                width="14"
                height="14"
                decoding="async"
              >
            </a>
          </div>
        </li>
      </ul>
    </section>

    <!-- 客户赞誉 -->
    <section class="gr-section gr-section--clients" aria-label="客户赞誉">
      <h2 class="gr-clients__title">
        客户赞誉
        <span class="gr-clients__title-bar" aria-hidden="true" />
      </h2>
      <ul class="gr-clients__grid">
        <li
          v-for="item in clientPraiseLogos"
          :key="item.id"
          class="gr-clients__card"
        >
          <img
            class="gr-clients__logo"
            :src="item.src"
            alt=""
            role="presentation"
            loading="lazy"
            decoding="async"
          >
        </li>
      </ul>
    </section>

    <AtsSiteFooter />
  </div>
</template>

<script>
import AtsSiteFooter from '@/components/ats/AtsSiteFooter.vue'
import AtsHeroBackground from '@/components/ats/AtsHeroBackground.vue'

/**
 * 全球救援专题页：素材来自 `src/assets/GlobalRescue`（英文命名）
 */
export default {
  name: 'GlobalRescue',
  components: {
    AtsSiteFooter,
    AtsHeroBackground
  },
  data () {
    return {
      activeResourceTab: 'domestic',
      assets: {
        heroBg: require('@/assets/GlobalRescue/hero_bg_earth_network.png'),
        cardGlobal: require('@/assets/GlobalRescue/card_feature_global_rescue.png'),
        cardIntegrated: require('@/assets/GlobalRescue/card_feature_integrated_rescue.png'),
        cardSmart: require('@/assets/GlobalRescue/card_feature_smart_rescue.png'),
        cardEmergency: require('@/assets/GlobalRescue/card_feature_emergency_guarantee.png'),
        sectionAdvantagesBg: require('@/assets/GlobalRescue/section_advantages_bg.png'),
        sectionGlobe: require('@/assets/GlobalRescue/section_global_resource_globe.png'),
        sectionGlobalResourceBg: require('@/assets/GlobalRescue/section_global_resource_bg.png'),
        sectionCasesBg: require('@/assets/GlobalRescue/section_service_cases_bg.png'),
        uiPillTabActive: require('@/assets/GlobalRescue/ui_pill_tab_active.png'),
        iconStandardPhone: require('@/assets/GlobalRescue/icon_standard_phone.png'),
        iconStandardMedical: require('@/assets/GlobalRescue/icon_standard_medical.png'),
        iconStandardClock: require('@/assets/GlobalRescue/icon_standard_clock.png'),
        iconStandardAlert: require('@/assets/GlobalRescue/icon_standard_alert.png'),
        uiPillCoverage: require('@/assets/GlobalRescue/ui_pill_coverage.png'),
        uiMetricPill: require('@/assets/GlobalRescue/ui_metric_pill.png'),
        uiMetricPillWide: require('@/assets/GlobalRescue/ui_metric_pill_wide.png'),
        uiDividerDot: require('@/assets/GlobalRescue/ui_divider_dot.png'),
        iconMetricPoint: require('@/assets/GlobalRescue/icon_metric_rescue_point.png'),
        iconMetricHelicopter: require('@/assets/GlobalRescue/icon_metric_helicopter.png'),
        iconMetricHospital: require('@/assets/GlobalRescue/icon_metric_hospital.png'),
        iconBrandMarkSmall: require('@/assets/GlobalRescue/icon_brand_mark_small.png'),
        iconArrowGreen: require('@/assets/GlobalRescue/icon_link_arrow_green.png'),
        iconArrowRightSmall: require('@/assets/GlobalRescue/icon_arrow_right_small.png'),
        building: require('@/assets/AbouttheSavior/section_global_services_building.png')
      }
    }
  },
  computed: {
    /**
     * 四大能力卡片（对齐设计稿：全球覆盖 / 一键救援 / 双语星使 / 综合服务）
     * @returns {Array<{ title: string, desc: string, img: string, icon: string }>}
     */
    featureCards () {
      const a = this.assets
      return [
        {
          title: '全球覆盖',
          desc: '60余万个救援服务网点覆盖全球233个国家和地区，含战乱、经济制裁的国家和地区。',
          img: a.cardGlobal,
          icon: a.iconMetricPoint
        },
        {
          title: '一键救援',
          desc: '用户可通过大救星APP或小程序首页的“一键救援”按键快速发起呼救。',
          img: a.cardIntegrated,
          icon: a.iconMetricHelicopter
        },
        {
          title: '双语星使',
          desc: '熟练使用中文及当地语言的“双语星使”，可为全球用户提供陪同、代办等各类贴心服务。',
          img: a.cardSmart,
          icon: a.iconMetricHospital
        },
        {
          title: '综合服务',
          desc: '平台整合全球救援、医疗、法律安保、保险等资源，打造一体化综合服务模式。',
          img: a.cardEmergency,
          icon: a.iconBrandMarkSmall
        }
      ]
    },
    /** @returns {Array<{ id: string, label: string }>} */
    resourceTabs () {
      return [
        { id: 'domestic', label: '国内分公司站点' },
        { id: 'network', label: '全球救援服务网络' },
        { id: 'overseas', label: '全球海外代表' },
        { id: 'alliance', label: '合作联盟组织' }
      ]
    },
    /**
     * 覆盖范围（顶行宽胶囊）
     * @returns {{ countries: number, cities: number }}
     */
    resourceCoverage () {
      return { countries: 137, cities: 820 }
    },
    /**
     * 资源指标胶囊（第二行）
     * @returns {Array<{ prefix: string, value: string, suffix: string }>}
     */
    resourceMetrics () {
      return [
        { prefix: '服务商', value: '1085', suffix: '家' },
        { prefix: '城市', value: '344', suffix: '个' },
        { prefix: '平台星使', value: '3130', suffix: '名' },
        { prefix: '覆盖国家', value: '134', suffix: '个' }
      ]
    },
    /**
     * 地球图上的三项大数字
     * @returns {Array<{ value: string, label: string, accent?: boolean }>}
     */
    resourceStats () {
      return [
        { value: '1234', label: '境外服务商', accent: false },
        { value: '109', label: '外国驻华使馆', accent: true },
        { value: '1872', label: '境外园区', accent: false }
      ]
    },
    /**
     * 服务标准四卡（2×2 栅格，对齐设计稿文案）
     * @returns {Array<{ title: string, metric: string, desc: string, icon: string }>}
     */
    serviceStandards () {
      const a = this.assets
      return [
        {
          title: '电话接通率',
          metric: '≥99%',
          desc: '24小时中英双语服务，确保呼叫中心电话接通率高达99%',
          icon: a.iconStandardAlert
        },
        {
          title: '在线平均等待时长',
          metric: '≤10s',
          desc: '客户在线平均等待时长≤10s, 积极响应客户需求',
          icon: a.iconStandardClock
        },
        {
          title: '派工时长',
          metric: '≤15min',
          desc: '高效协调平台资源，15分钟内迅速做出救援服务安排计划',
          icon: a.iconStandardMedical
        },
        {
          title: '投诉率',
          metric: '≤0.1%',
          desc: '2小时内查明投诉原因并做出投诉处理方案，客户满意度99.9%',
          icon: a.iconStandardPhone
        }
      ]
    },
    /**
     * 服务优势三卡（首卡绿色强调）
     * @returns {Array<{ title: string, desc: string, primary?: boolean }>}
     */
    serviceAdvantages () {
      return [
        {
          title: '服务流程可视化',
          desc: '通过大数据救援云平台链接指挥中心、救援机构及监管机构三方，记录每一条救援节点，做到服务全流程可视、可控',
          primary: true
        },
        {
          title: '产品服务定制化',
          desc: '除在售清单外，支持客户根据自身需求匹配合作模式、权益范围等内容，定制专属清单产品',
          primary: false
        },
        {
          title: '系统支持灵活化',
          desc: '支持为保司或企业客户提供系统打通系统工具等服务，助力高效对接',
          primary: false
        }
      ]
    },
    /**
     * 服务案例三卡（对齐设计稿）
     * @returns {Array<{ title: string, desc: string, cover: string }>}
     */
    serviceCases () {
      const a = this.assets
      const desc =
        '中共中央台办、国务院台办主任宋涛6月15日在江苏苏州出席海峡两岸特色产业融合发展论坛表示，两岸的事是两岸同胞的家里事，两岸的事要由两岸同胞商量着办。'
      const cover = a.sectionGlobalResourceBg
      return [
        { title: '国台办：两岸的事是两岸同胞的家里事', desc, cover },
        { title: '国台办：两岸的事是两岸同胞的家里事', desc, cover },
        { title: '国台办：两岸的事是两岸同胞的家里事', desc, cover }
      ]
    },
    /**
     * 服务案例正文区背景（`ui_pill_tab_active`）
     * @returns {{ backgroundImage: string, backgroundSize: string, backgroundRepeat: string, backgroundPosition: string, backgroundColor: string }}
     */
    caseBodyStyle () {
      return {
        backgroundColor: '#ffffff',
        backgroundImage: `url(${this.assets.uiPillTabActive})`,
        backgroundSize: '100% 100%',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center'
      }
    },
    /**
     * 客户赞誉 Logo 占位（5×3，暂无真实合作方标识）
     * @returns {Array<{ id: number, src: string }>}
     */
    clientPraiseLogos () {
      const placeholder = this.assets.iconBrandMarkSmall
      return Array.from({ length: 15 }, (_, index) => ({
        id: index + 1,
        src: placeholder
      }))
    },
    /**
     * 区块背景：按 878px 区域完整铺满（不用 cover，避免裁切）
     * @returns {{ backgroundColor: string, backgroundImage: string, backgroundSize: string, backgroundPosition: string, backgroundRepeat: string }}
     */
    resourceSectionStyle () {
      return {
        backgroundColor: '#f4f9fb',
        backgroundImage: `url(${this.assets.sectionAdvantagesBg})`,
        backgroundSize: '100% 100%',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat'
      }
    },
    /**
     * 顶行宽指标胶囊背景
     * @returns {{ backgroundImage: string, backgroundSize: string, backgroundRepeat: string, backgroundPosition: string }}
     */
    resourceCoveragePillStyle () {
      return {
        backgroundImage: `url(${this.assets.uiMetricPillWide})`,
        backgroundSize: '100% 100%',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center'
      }
    },
    /**
     * 四指标胶囊背景
     * @returns {{ backgroundImage: string, backgroundSize: string, backgroundRepeat: string, backgroundPosition: string }}
     */
    resourceMetricPillStyle () {
      return {
        backgroundImage: `url(${this.assets.uiMetricPill})`,
        backgroundSize: '100% 100%',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center'
      }
    },
    /**
     * 服务优势区块背景：`section_global_resource_globe`，高度 643px
     * @returns {{ backgroundColor: string, backgroundImage: string, backgroundSize: string, backgroundPosition: string, backgroundRepeat: string }}
     */
    advantagesSectionStyle () {
      return {
        backgroundColor: '#0a1e2e',
        backgroundImage: `url(${this.assets.sectionGlobe})`,
        backgroundSize: '100% 100%',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat'
      }
    },
    /**
     * 服务优势卡片底图（`section_service_cases_bg`）
     * @returns {{ backgroundImage: string, backgroundSize: string, backgroundPosition: string, backgroundRepeat: string }}
     */
    advantageCardStyle () {
      return {
        backgroundImage: `url(${this.assets.sectionCasesBg})`,
        backgroundSize: '100% 100%',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }
    },
    /**
     * Tab 选中态背景（`ui_pill_coverage`）
     * @returns {{ backgroundImage: string, backgroundSize: string, backgroundRepeat: string, backgroundPosition: string, backgroundColor: string }}
     */
    resourceTabActiveStyle () {
      return {
        backgroundColor: 'transparent',
        backgroundImage: `url(${this.assets.uiPillCoverage})`,
        backgroundSize: '100% 100%',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center'
      }
    }
  },
  methods: {
    /**
     * @param {{ id: string }} item
     * @returns {void}
     */
    /**
     * 服务标准卡片：680×174，渐变底 + 右下角装饰图标背景
     * @param {{ icon: string }} item - 标准项
     * @returns {{ backgroundImage: string, backgroundSize: string, backgroundPosition: string, backgroundRepeat: string }}
     */
    standardCardStyle (item) {
      return {
        backgroundImage: `url(${item.icon}), linear-gradient(90deg, #eceff1 0%, #f6f8f9 42%, #ffffff 100%)`,
        backgroundSize: 'auto 100%, 100% 100%',
        backgroundPosition: 'right center, 0 0',
        backgroundRepeat: 'no-repeat, no-repeat'
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../components/ats/mixins';
$green: #256A30;
$text-dark: #1a2226;
$body-grey: #333333;

.gr-page {
  min-height: 100vh;
  background: #ffffff;
  color: $text-dark;
  font-family: 'Microsoft YaHei', 'PingFang SC', 'Helvetica Neue', sans-serif;
  -webkit-font-smoothing: antialiased;
}

/** 首屏：与 `about-the-savior` 的 `.ats-hero` 同尺寸、同底图展示方式（871px + cover + center bottom） */
.gr-hero-header {
  position: relative;
  height: 871px;
  min-height: 871px;
  max-height: 871px;
}

.gr-hero {
  position: relative;
  height: 100%;
  min-height: 871px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  color: #fff;
  overflow: hidden;
}

.gr-hero__bg {
  background-size: cover;
  background-position: center bottom;
}

.gr-hero__mask {
  position: absolute;
  inset: 0;
  z-index: 1;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.04) 0%,
    rgba(0, 0, 0, 0) 38%,
    rgba(0, 0, 0, 0) 62%,
    rgba(6, 14, 11, 0.12) 100%
  );
  pointer-events: none;
}

.gr-hero__content {
  position: relative;
  z-index: 2;
  max-width: 1040px;
  margin: 0 auto;
  width: 100%;
  padding: 0 28px;
  box-sizing: border-box;
  text-align: center;
}

.gr-hero__title {
  margin: 0 0 50px;
  font-size: 56px;
  font-weight: 800;
  line-height: 1.22;
  letter-spacing: 0.08em;
  color: #fff;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5), 0 4px 28px rgba(0, 0, 0, 0.4);
}

.gr-hero__subtitle {
  margin: 0 auto;
  font-size: 24px;
  font-weight: 400;
  line-height: 1.75;
  letter-spacing: 0.06em;
  color: #fff;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.45), 0 2px 16px rgba(0, 0, 0, 0.35);
}

.gr-section {
  margin: 0 auto;
  padding: 72px max(24px, calc((100% - 1160px) / 2));
  box-sizing: border-box;
  max-width: none;
}

.gr-h2 {
  margin: 0 0 32px;
  font-size: 42px;
  font-weight: 700;
  line-height: 1.25;
  color: $text-dark;
  text-align: center;
}

.gr-h2--on-dark {
  color: #ffffff;
}

.gr-h2--bar {
  position: relative;
  padding-right: 40px;
  display: inline-block;
  left: 50%;
  transform: translateX(-50%);
  &::after {
    content: '';
    display: inline-block;
    width: 10px;
    height: 0.85em;
    margin-left: 30px;
    vertical-align: middle;
    border-radius: 2px;
    background: $green;
  }
}

/* 四大能力 */
.gr-section--features {
  padding-top: 56px;
  padding-bottom: 56px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.gr-features {
  list-style: none;
  margin: 0 auto;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(4, 300px);
  justify-content: center;
  gap: 20px;
  width: fit-content;
  max-width: 100%;
  box-sizing: border-box;
}

/** 能力卡片：300×500；padding 计入盒模型；底图绝对铺满，文案在 padding 内排布 */
.gr-features__item {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  flex-shrink: 0;
  width: 300px;
  height: 500px;
  box-sizing: border-box;
  padding: 50px 40px;
  border-radius: 0;
  overflow: hidden;
  background: #0a1628;
}

.gr-features__img {
  position: absolute;
  inset: 0;
  z-index: 0;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
}

/** 底部文案区轻渐变；宽度随 item 的 padding 内缩，不再贴边 */
.gr-features__body {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-end;
  width: 100%;
  box-sizing: border-box;
  padding: 72px 0 0;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0.35) 28%,
    rgba(0, 0, 0, 0.72) 100%
  );
  color: #ffffff;
  pointer-events: none;
}

.gr-features__icon {
  width: 38px;
  height: 30px;
  margin-bottom: 14px;
  object-fit: contain;
  flex-shrink: 0;
  display: block;
}

.gr-features__title {
  margin: 35px 0;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.35;
  color: #ffffff;
  text-align: left;
}

.gr-features__desc {
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.75;
  color: rgba(255, 255, 255, 0.92);
  text-align: left;
}

/* 全球资源布局：整体高度 878px；背景随区块宽高拉伸铺满（见 resourceSectionStyle） */
.gr-section--resource {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 100%;
  height: 878px;
  min-height: 878px;
  box-sizing: border-box;
  padding-top: 64px;
  padding-bottom: 48px;
  overflow: hidden;
  background-origin: padding-box;
  background-clip: padding-box;
}

.gr-resource__title {
  margin-bottom: 28px;
  margin-top: 70px;
}

/** Tab 外胶囊：890×70，四项均分 */
.gr-resource__tabbar {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: stretch;
  gap: 4px;
  width: 890px;
  max-width: 100%;
  height: 70px;
  margin: 0 auto;
  padding: 5px 8px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 999px;
  background: #ffffff;
  box-sizing: border-box;
}

.gr-resource__tab {
  flex: 1 1 0;
  min-width: 0;
  width: 0;
  height: 60px;
  padding: 0 10px;
  border: none;
  border-radius: 999px;
  background: transparent;
  font-size: 16px;
  font-weight: 600;
  line-height: 60px;
  text-align: center;
  color: $text-dark;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  box-sizing: border-box;
  &.is-active {
    color: #ffffff;
  }
  &:not(.is-active):hover {
    color: $green;
  }
  @include ats-hover-tab;
}

/** 顶行覆盖胶囊：360×70，上下间距 50px */
.gr-resource__coverage {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 360px;
  max-width: 100%;
  height: 70px;
  margin: 50px auto;
  padding: 0 24px;
  box-sizing: border-box;
}

.gr-resource__pills {
  list-style: none;
  margin: 0 auto 28px;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px 14px;
  max-width: 1000px;
}

.gr-resource__pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-width: 0;
  height: 48px;
  padding: 0 22px;
  box-sizing: border-box;
}

.gr-resource__dot {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  object-fit: contain;
  display: block;
}

.gr-resource__pill-text {
  font-size: 16px;
  font-weight: 400;
  line-height: 1.4;
  color: $text-dark;
  white-space: nowrap;
}

.gr-resource__num {
  font-style: normal;
  font-weight: 700;
  color: $green;
}

/** 三项大数字 */
.gr-resource__stats-wrap {
  width: 100%;
  max-width: 1100px;
  margin-left: auto;
  margin-right: auto;
  padding-bottom: 8px;
}

.gr-resource__stats {
  list-style: none;
  margin: 152px 0 0;
  padding: 0;
  display: flex;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: flex-start;
  gap: 120px;
  li {
    text-align: center;
    min-width: 0;
  }
}

.gr-resource__stat-value {
  display: block;
  font-size: 42px;
  font-weight: 800;
  line-height: 1.1;
  color: $text-dark;
  letter-spacing: 0.02em;
}

.gr-resource__stat--accent .gr-resource__stat-value {
  color: $green;
}

.gr-resource__stat-label {
  display: block;
  margin-top: 8px;
  font-size: 18px;
  font-weight: 400;
  line-height: 1.4;
  color: $body-grey;
}

/* 服务标准：2×2 卡片，左文右下大图标 */
.gr-section--standards {
  background: #ffffff;
  text-align: center;
}

.gr-standards__section-title {
  margin-bottom: 40px;
}

.gr-standards {
  list-style: none;
  margin: 0 auto;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(2, 680px);
  justify-content: center;
  gap: 28px;
  width: 100%;
  max-width: 1388px;
  box-sizing: border-box;
}

/** 服务标准卡：680×174，装饰图见 standardCardStyle */
.gr-standards__card {
  position: relative;
  width: 680px;
  max-width: 100%;
  height: 174px;
  min-height: 174px;
  padding: 28px 32px;
  box-sizing: border-box;
  overflow: hidden;
  border: none;
  border-radius: 8px;
  background-color: #ffffff;
  text-align: left;
}

.gr-standards__body {
  position: relative;
  z-index: 1;
  max-width: calc(100% - 88px);
}

.gr-standards__head {
  display: flex;
  align-items: baseline;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 8px 20px;
  margin-bottom: 14px;
}

.gr-standards__title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.35;
  color: $text-dark;
}

.gr-standards__metric {
  margin: 0;
  flex-shrink: 0;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.35;
  color: $green;
}

.gr-standards__desc {
  margin: 0;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.75;
  color: #666666;
}

/* 服务优势：643px 高，背景 section_global_resource_globe */
.gr-section--advantages {
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 643px;
  min-height: 643px;
  box-sizing: border-box;
  padding: 0 max(24px, calc((100% - 1160px) / 2)) 0;
  overflow: hidden;
  color: #ffffff;
}

.gr-advantages__section-title {
  flex-shrink: 0;
  margin: 120px 0 0;
  color: #ffffff;
  text-align: center;
}

/** 三卡贴齐区块底部（标题在上，卡片区 margin-top: auto） */
.gr-advantages {
  list-style: none;
  margin: auto auto 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(3, 440px);
  justify-content: center;
  gap: 28px;
  width: 100%;
  max-width: 1384px;
  flex: 0 0 auto;
  align-self: center;
  align-items: start;
  box-sizing: border-box;
}

/** 服务优势卡：440×387 */
.gr-advantages__card {
  --gr-advantage-card-px: 32px;
  position: relative;
  width: 440px;
  max-width: 100%;
  height: 387px;
  min-height: 387px;
  padding: 0 var(--gr-advantage-card-px) 32px;
  color: $text-dark;
  box-sizing: border-box;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  text-align: left;
  overflow: hidden;
}

/** 首卡顶栏装饰图：贴 li 顶边、横向 100% 铺满（抵消左右 padding，不改动 padding） */
.gr-advantages__card-accent {
  position: absolute;
  top: 0;
  left: calc(-1 * var(--gr-advantage-card-px));
  z-index: 2;
  display: block;
  width: calc(100% + 2 * var(--gr-advantage-card-px));
  max-width: none;
  height: auto;
  margin: 0;
  padding: 0;
  object-fit: cover;
  object-position: center top;
  pointer-events: none;
  user-select: none;
}

.gr-advantages__num {
  position: relative;
  z-index: 1;
  display: block;
  margin: 50px 0 40px;
  font-size: 50px;
  font-weight: 800;
  line-height: 1;
  color: #c8cdd2;
}

.gr-advantages__card--primary .gr-advantages__num {
  color: $green;
}

.gr-advantages__card-title {
  position: relative;
  z-index: 1;
  margin: 0 0 14px;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.35;
  color: $text-dark;
}

.gr-advantages__desc {
  position: relative;
  z-index: 1;
  margin: 0;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.75;
  color: #666666;
  margin-top: 30px;
}

/* 服务案例 */
.gr-section--cases {
  background: #ffffff;
  text-align: left;
  padding-top: 0;
  padding-bottom: 72px;
}

.gr-cases__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  max-width: 1384px;
  width: 100%;
  margin: 120px auto 50px;
  box-sizing: border-box;
}

.gr-cases__section-title {
  margin: 0;
  font-size: 42px;
  font-weight: 700;
  line-height: 1.25;
  color: $text-dark;
  text-align: left;
}

.gr-cases__more-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-shrink: 0;
  height: 40px;
  padding: 0 20px;
  border: 1px solid #d8dce0;
  border-radius: 8px;
  background: #ffffff;
  font-size: 16px;
  font-weight: 400;
  line-height: 1;
  color: $text-dark;
  text-decoration: none;
  box-sizing: border-box;
  @include ats-hover-outline;
  &:hover {
    border-color: $green;
    color: $green;
  }
}

.gr-cases__more-btn-icon {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  color: $text-dark;
}

.gr-cases__more-btn-icon-svg {
  display: block;
}

.gr-cases {
  list-style: none;
  margin: 0 auto;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 440px));
  justify-content: center;
  gap: 28px;
  max-width: 1384px;
  width: 100%;
  box-sizing: border-box;
}

.gr-cases__card {
  display: flex;
  flex-direction: column;
  background: #f5f5f5;
  border-radius: 12px;
  overflow: hidden;
  box-sizing: border-box;
}

/** 封面图贴顶铺满卡宽，不受正文 padding 影响 */
.gr-cases__cover {
  display: block;
  width: 100%;
  height: 240px;
  flex-shrink: 0;
  object-fit: cover;
  object-position: center center;
  border-radius: 12px 12px 0 0;
}

.gr-cases__body {
  flex: 1;
  padding: 24px 28px 28px;
  box-sizing: border-box;
  background-color: #ffffff;
}

.gr-cases__title {
  margin: 40px 0 20px;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.45;
  color: $text-dark;
}

.gr-cases__desc {
  margin: 0 0 20px;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.75;
  color: #666666;
}

.gr-cases__link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 16px;
  font-weight: 600;
  line-height: 1;
  color: $green;
  text-decoration: none;
  @include ats-hover-text-link;
  img {
    display: block;
    flex-shrink: 0;
  }
  &:hover {
    color: $green;
    opacity: 0.92;
  }
}

/* 客户赞誉 */
.gr-section--clients {
  background: #ffffff;
  text-align: left;
  padding-top: 0;
  padding-bottom: 72px;
}

.gr-clients__title {
  display: inline-flex;
  align-items: center;
  margin: 120px 0 50px;
  font-size: 42px;
  font-weight: 700;
  line-height: 1.25;
  color: $text-dark;
}

.gr-clients__title-bar {
  display: inline-block;
  width: 10px;
  height: 0.85em;
  margin-left: 30px;
  border-radius: 2px;
  background: $green;
  flex-shrink: 0;
}

.gr-clients__grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 20px;
  max-width: 1384px;
  width: 100%;
  margin: 0 auto;
  padding: 0;
  list-style: none;
  box-sizing: border-box;
}

.gr-clients__card {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100px;
  padding: 24px 20px;
  background: #ffffff;
  border: 1px solid #e8ecef;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(15, 35, 52, 0.06);
  box-sizing: border-box;
}

.gr-clients__logo {
  display: block;
  max-width: 120px;
  max-height: 48px;
  width: auto;
  height: auto;
  object-fit: contain;
  opacity: 0.45;
  filter: grayscale(100%);
}

@media (max-width: 960px) {
  .gr-hero-header {
    height: auto;
    min-height: 480px;
    max-height: none;
  }
  .gr-hero {
    height: auto;
    min-height: 480px;
    max-height: none;
  }
  .gr-features {
    grid-template-columns: repeat(2, minmax(0, 300px));
    width: 100%;
    justify-content: center;
  }
  .gr-features__item {
    width: min(300px, 100%);
    height: 500px;
  }
  .gr-standards {
    grid-template-columns: minmax(0, 1fr);
    gap: 16px;
  }
  .gr-standards__card {
    width: 100%;
    height: auto;
    min-height: 174px;
    padding: 22px 20px;
  }
  .gr-standards__body {
    max-width: 100%;
  }
  .gr-standards__head {
    gap: 6px 12px;
    margin-bottom: 10px;
  }
  .gr-standards__title,
  .gr-standards__metric {
    font-size: 18px;
  }
  .gr-standards__desc {
    font-size: 15px;
  }
  .gr-section--advantages {
    height: auto;
    min-height: 0;
    padding: 48px 20px 56px;
    background-size: 100% auto !important;
    background-position: center bottom !important;
  }
  .gr-advantages {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  .gr-advantages__card {
    --gr-advantage-card-px: 24px;
    width: 100%;
    height: auto;
    min-height: 387px;
    padding: 0 var(--gr-advantage-card-px) 28px;
  }
  .gr-advantages__num {
    margin-top: 32px;
    margin-bottom: 24px;
    font-size: 40px;
  }
  .gr-advantages__card-title {
    font-size: 20px;
  }
  .gr-advantages__desc {
    font-size: 22px;
  }
  .gr-cases__head {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  .gr-cases__section-title {
    font-size: 32px;
  }
  .gr-cases {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  .gr-cases__cover {
    height: 200px;
  }
  .gr-cases__body {
    padding: 20px;
  }
  .gr-section--resource {
    height: auto;
    min-height: 640px;
    overflow: visible;
    padding-top: 56px;
    padding-bottom: 56px;
    background-size: 100% auto !important;
    background-position: center bottom !important;
  }
  .gr-resource__tabbar {
    width: 100%;
    height: auto;
    min-height: 70px;
    flex-wrap: wrap;
    border-radius: 20px;
    padding: 8px;
    gap: 6px;
  }
  .gr-resource__tab {
    flex: 1 1 calc(50% - 6px);
    width: auto;
    min-width: calc(50% - 6px);
    height: 44px;
    line-height: 44px;
    padding: 0 8px;
    font-size: 14px;
  }
  .gr-resource__coverage {
    width: 100%;
    max-width: 360px;
    height: auto;
    min-height: 70px;
    margin: 32px auto;
    padding: 12px 16px;
    white-space: normal;
  }
  .gr-resource__stats {
    margin-top: 48px;
  }
  .gr-resource__stat-value {
    font-size: 36px;
  }
  .gr-resource__pill-text {
    white-space: normal;
    text-align: center;
  }
  .gr-resource__pills {
    gap: 10px;
  }
  .gr-resource__pill {
    width: calc(50% - 6px);
    padding: 0 12px;
  }
  .gr-resource__stats {
    flex-wrap: wrap;
    gap: 24px 40px;
  }
  .gr-clients__title {
    margin-top: 72px;
    margin-bottom: 32px;
    font-size: 32px;
  }
  .gr-clients__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }
  .gr-clients__card {
    min-height: 80px;
    padding: 16px 12px;
  }
  .gr-h2 {
    font-size: 32px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/globalRescue/globalRescue.vue